import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import { createUsersBusinessesPortfolio } from 'lib/api';
import {
  getCollectionManagementOptionsByStateId,
  getCollectionManagementStates,
} from 'lib/api/collectionManagementStates';
import {
  createMultipleCollectionManagement,
  getDocumentsDataForCollection,
  getExecutivesForDocumentCollection,
} from 'lib/api/collections';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import { loggerDev, showAlert } from 'lib/functions/utils';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Actions } from './components/Actions';
import { CollectionState, OptionsGrid } from './components/OptionsGrid';

interface propsType {
  openModal: boolean;
  setOpenModal: (open: boolean) => void;
  setUp: () => void;
  documentsWithId: any[];
}

const NewMultipleManagement: React.FC<propsType> = (props: propsType) => {
  const { openModal, setOpenModal, setUp, documentsWithId } = props;
  const [option, setOption] = useState<any>(null);
  const [description, setDescription] = useState('');
  const [checkFields, setCheckField] = useState(false);
  const [options, setOptions] = useState([]);
  const [disabled, setDisabled] = useState(false);
  const [stateId, setStateId] = useState<number>(0);
  const [stateName, setStateName] = useState('');
  const [stateOptions, setStateOptions] = useState<CollectionState[]>([]);
  const [completedCheckbox, setCompletedCheckbox] = useState(false);
  const [loading, setLoading] = useState(false);
  const [contactMethod, setContactMethod] = useState('');
  const today = moment()
    .utcOffset(new Date().getTimezoneOffset() * -1)
    .format('YYYY-MM-DD');
  const [dueDate, setDueDate] = useState(today);
  const [completedDate, setCompletedDate] = useState(today);
  const [documentHasExecutives, setDocumentHasExecutives] = useState(false);
  const [executivesOptions, setExecutivesOptions] = useState([]);
  const [executiveId, setExecutiveId] = useState(0);
  const [isManager, setIsManager] = useState(false);
  const [byStakeholder, setByStakeholder] = useState(false);
  const [portfolioExecutiveId, setPortfolioExecutiveId] = useState(0);
  const [possiblePaymentDate, setPossiblePaymentDate] = useState('none');
  const [documentsForMultiple, setDocumentsForMultiple] = useState<any[]>([]);
  const [managementsPossiblePaymentDate, setManagementsPossiblePaymentDate] =
    useState<any[]>([]);
  const dispatch = useDispatch();

  const checkInputFields = () => {
    if (
      !description.length ||
      option === '' ||
      stateId === 0 ||
      contactMethod === '' ||
      dueDate === ''
    ) {
      return true;
    }
    return false;
  };

  const handleSend = async () => {
    setCheckField(checkInputFields());
    if (checkInputFields()) return;
    setLoading(true);
    try {
      const data: any = { managements: [] };
      documentsForMultiple.forEach((document: any) => {
        const possiblePaymentDateForDocument =
          managementsPossiblePaymentDate.find(
            (management: any) => management.documentId === document.id,
          )?.possiblePaymentDate;
        data.managements.push({
          description: description,
          type: contactMethod,
          optionId: option,
          documentId: document.id,
          state: stateName,
          dueDate: new Date(dueDate).toISOString(),
          completedAt: completedCheckbox
            ? new Date(completedDate).toISOString()
            : null,
          executiveId: executiveId,
          possiblePaymentDate: possiblePaymentDateForDocument || 'none',
        });
      });
      await createMultipleCollectionManagement(dispatch, data);
      setOpenModal(false);
      showAlert(
        dispatch,
        'success',
        'Tus gestiones de cobranza han sido creadas',
      );
      await new Promise((resolve) => setTimeout(resolve, 1000));
      await setUp();
    } catch (error) {
      showAlert(
        dispatch,
        'error',
        'Hubo un error al crear la gestión de cobranza',
      );
      loggerDev(error);
    }
    setLoading(false);
  };

  const handleSelectState = async (value: number) => {
    setDisabled(true);
    setStateId(value);
    const selectedState = stateOptions.find((element) => element.id === value);
    setStateName(selectedState ? selectedState.name : '');
    await getOptions(value);
    setDisabled(false);
  };

  const getOptions = async (selectedStateId: number) => {
    setLoading(true);
    try {
      const result = await getCollectionManagementOptionsByStateId(
        dispatch,
        selectedStateId,
      );
      setOptions(result);
    } catch (error) {
      loggerDev(error);
    }
    setLoading(false);
  };

  const handleAssignExecutive = async () => {
    setLoading(true);
    try {
      await createUsersBusinessesPortfolio(dispatch, portfolioExecutiveId, {
        businessIdentifiers: byStakeholder
          ? [documentsForMultiple[0].stakeholderIdentifier]
          : [documentsForMultiple[0].businessIdentifier],
      });
      showAlert(dispatch, 'success', 'Ejecutivo asignado correctamente');
      void setUpOptions();
    } catch (error) {
      showAlert(dispatch, 'error', 'Error al asignar ejecutivo');
      loggerDev(error);
    }
    setLoading(false);
  };

  const setUpOptions = async () => {
    try {
      const result = await getCollectionManagementStates(dispatch);
      setStateOptions(result);
      const query = `businessIdentifier=${documentsForMultiple[0].businessIdentifier}&stakeholderIdentifier=${documentsForMultiple[0].stakeholderIdentifier}`;
      const resultExecutives = await getExecutivesForDocumentCollection(
        dispatch,
        query,
      );
      setDocumentHasExecutives(resultExecutives.documentHasExecutives);
      setExecutivesOptions(resultExecutives.executives);
      setIsManager(resultExecutives.isManager);
      setByStakeholder(resultExecutives.byStakeholder);
      setPortfolioExecutiveId(0);
      setExecutiveId(0);
    } catch (error) {
      loggerDev(error);
    }
  };

  const getDocumentsForMultiple = async () => {
    setLoading(true);
    const documentIds = documentsWithId.map((document: any) => {
      return document.id;
    });
    const documentsForMultiple = await getDocumentsDataForCollection(dispatch, {
      documentIds: documentIds,
    });
    setDocumentsForMultiple(documentsForMultiple);
    setManagementsPossiblePaymentDate(
      documentsForMultiple.map((document: any) => {
        return {
          documentId: document.id,
          possiblePaymentDate: 'none',
        };
      }),
    );
    setLoading(false);
  };

  const setUpDialog = async () => {
    setLoading(true);
    await setUpOptions();
    setLoading(false);
  };

  const setUpDocuments = async () => {
    await getDocumentsForMultiple();
  };

  useEffect(() => {
    void setUpDocuments();
  }, []);

  useEffect(() => {
    if (documentsForMultiple.length > 0) {
      void setUpDialog();
    }
  }, [documentsForMultiple]);

  if (loading) return <CenteredCircularProgress type="dialog" />;

  return (
    <Dialog open={openModal} maxWidth="md">
      <DialogTitle> Crear nueva gestión múltiple de cobranza</DialogTitle>
      <DialogContent style={{ minWidth: '500px' }}>
        <OptionsGrid
          executiveId={executiveId}
          documentHasExecutives={documentHasExecutives}
          byStakeholder={byStakeholder}
          documentsForMultiple={documentsForMultiple}
          checkFields={checkFields}
          option={option}
          setOption={setOption}
          setExecutiveId={setExecutiveId}
          loading={loading}
          executivesOptions={executivesOptions}
          portfolioExecutiveId={portfolioExecutiveId}
          setPortfolioExecutiveId={setPortfolioExecutiveId}
          stateId={stateId}
          handleSelectState={handleSelectState}
          stateOptions={stateOptions}
          isManager={isManager}
          completedCheckbox={completedCheckbox}
          setCompletedCheckbox={setCompletedCheckbox}
          completedDate={completedDate}
          dueDate={dueDate}
          description={description}
          setDescription={setDescription}
          contactMethod={contactMethod}
          disabled={disabled}
          setDueDate={setDueDate}
          setCompletedDate={setCompletedDate}
          setContactMethod={setContactMethod}
          options={options}
          possiblePaymentDate={possiblePaymentDate}
          setPossiblePaymentDate={setPossiblePaymentDate}
          managementsPossiblePaymentDate={managementsPossiblePaymentDate}
          setManagementsPossiblePaymentDate={setManagementsPossiblePaymentDate}
        />
      </DialogContent>
      <Actions
        handleClose={() => setOpenModal(false)}
        documentHasExecutives={documentHasExecutives}
        handleSend={handleSend}
        loading={loading}
        stateId={stateId}
        option={option}
        contactMethod={contactMethod}
        description={description}
        dueDate={dueDate}
        handleAssignExecutive={handleAssignExecutive}
        portfolioExecutiveId={portfolioExecutiveId}
      />
    </Dialog>
  );
};

export default NewMultipleManagement;
